"use client";
import axios, { AxiosError } from "axios";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { type SessionCart, type SessionCartItem } from "@/ui/types";
import { variantName } from "@/lib/variants";
import { Button } from "@/ui/atoms/Button";
import CheckoutButton from "@/ui/components/CheckoutButton";

const API_URL = process.env.NEXT_PUBLIC_MICROGRAPHER_API_URL;
const API_KEY = process.env.NEXT_PUBLIC_MICROGRAPHER_API_KEY;
const CDN = "https://d127u13u6yy1k7.cloudfront.net/static/images/products";

function formatMoney(amount: number) {
	return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
}

export default function Cart() {
	const [cart, setCart] = useState<SessionCart>();
	const [isLoading, setIsLoading] = useState(true);
	const [removing, setRemoving] = useState<string | null>(null);

	useEffect(() => {
		const fetchCartData = async () => {
			try {
				const response = await axios.get<SessionCart>(`${API_URL}/cart`, {
					withCredentials: true,
					headers: { "x-api-key": API_KEY },
				});
				const sessionCart = response.data;
				setCart(Array.isArray(sessionCart) ? sessionCart : []);
			} catch (error) {
				if (error instanceof AxiosError) console.error("AxiosError when fetching cart:", error);
				console.error("Error fetching cart data:", error);
			} finally {
				setIsLoading(false);
			}
		};
		fetchCartData();
	}, []);

	// Remove a line: update local state + notify the nav badge — NO full page reload (R.6.1).
	const removeItem = async (sku: string) => {
		setRemoving(sku);
		try {
			await axios.delete(`${API_URL}/cart/${encodeURIComponent(sku)}`, {
				withCredentials: true,
				headers: { "x-api-key": API_KEY },
			});
			setCart((prev) => (prev ? prev.filter((i) => i.sku !== sku) : prev));
			window.dispatchEvent(new Event("cart:changed"));
		} catch (error) {
			console.error("Error removing cart item:", error);
		} finally {
			setRemoving(null);
		}
	};

	if (isLoading) {
		return (
			<section className="mx-auto max-w-gallery px-5 py-section sm:px-8">
				<p className="label-caps text-ink-muted">Loading your selection…</p>
			</section>
		);
	}

	if (!cart || cart.length < 1) {
		return (
			<section className="mx-auto max-w-gallery px-5 py-section text-center sm:px-8">
				<h1 className="font-serif text-display font-light text-mg-black">Your cart is empty</h1>
				<p className="mx-auto mt-5 max-w-prose text-ink-muted">
					Nothing selected yet. The collection is waiting.
				</p>
				<div className="mt-10">
					<Button href="/shop">Enter the Collection</Button>
				</div>
			</section>
		);
	}

	const total =
		cart.reduce((t, item) => Number(t) + Number(item.price_cents) * Number(item.quantity), 0) / 100;

	return (
		<section className="mx-auto max-w-gallery px-5 py-section-sm sm:px-8">
			<header className="mb-12">
				<p className="label-caps mb-4 text-ink-muted">Your selection</p>
				<h1 className="font-serif text-display font-light text-mg-black">The Cart</h1>
			</header>

			<div className="grid gap-12 lg:grid-cols-[3fr_2fr] lg:gap-16">
				{/* Line items — cardless, hairline-separated. */}
				<ul
					data-testid="CartProductList"
					role="list"
					className="divide-y divide-mg-black/10 border-y border-mg-black/10"
				>
					{cart.map((item: SessionCartItem) => (
						<li key={item.sku} className="flex gap-6 py-8">
							<Link
								href={`/shop/${item.img_name}`}
								className="relative aspect-square h-28 w-28 flex-shrink-0 overflow-hidden bg-bone-deep sm:h-32 sm:w-32"
							>
								{item.img_name && (
									<Image
										src={`${CDN}/${item.img_name}/${item.img_name}-${item.orientation}-no-frame-no-mat.webp`}
										alt={item.product_name}
										fill
										sizes="128px"
										className="object-cover"
									/>
								)}
							</Link>
							<div className="flex flex-1 flex-col justify-between">
								<div className="flex justify-between gap-4">
									<div>
										<Link href={`/shop/${item.img_name}`}>
											<h2 className="font-serif text-xl font-medium text-mg-black transition-colors hover:text-mg-gold-deep">
												{item.product_name}
											</h2>
										</Link>
										<p className="label-caps mt-2 text-ink-faint">{variantName(item.size, item.tier)}</p>
										<p className="mt-1 text-sm text-ink-muted">Qty: {item.quantity}</p>
									</div>
									<p className="whitespace-nowrap font-serif text-lg text-mg-black">
										{formatMoney(Number(item.price_cents) / 100)}
									</p>
								</div>
								<div>
									<button
										type="button"
										onClick={() => removeItem(item.sku)}
										disabled={removing === item.sku}
										className="label-caps text-ink-faint underline decoration-mg-black/20 underline-offset-4 transition-colors hover:text-mg-black disabled:opacity-50"
									>
										{removing === item.sku ? "Removing…" : "Remove"}
									</button>
								</div>
							</div>
						</li>
					))}
				</ul>

				{/* Summary — quiet, sticky on desktop. */}
				<div className="lg:sticky lg:top-28 lg:self-start">
					<div className="border-t border-mg-black/10 pt-8">
						<div className="flex items-baseline justify-between">
							<p className="label-caps text-ink-muted">Total</p>
							<p className="font-serif text-3xl font-light text-mg-black">{formatMoney(total)}</p>
						</div>
						<p className="mt-3 text-sm text-ink-faint">Shipping and tax calculated at checkout.</p>
						<div className="mt-8">
							<CheckoutButton sessionCart={cart} page={"Cart"} />
						</div>
						<Link href="/shop" className="link-gallery mt-6 inline-block text-sm">
							Continue browsing
						</Link>
					</div>
				</div>
			</div>
		</section>
	);
}
